import { useState, useRef, useCallback } from "react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  apiGetCalendarEvent,
  apiUpdateCalendarEvent,
  apiDeleteCalendarEvent,
  apiUploadEventCoverImage,
  type CalendarEvent,
} from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import {
  format,
  parseISO,
  isSameDay,
} from "date-fns";
import {
  ChevronLeft,
  CalendarDays,
  MapPin,
  Clock,
  User,
  Link2,
  Camera,
  Edit2,
  Trash2,
  Plus,
  ExternalLink,
  Users,
  Megaphone,
  PartyPopper,
  Briefcase,
  Flag,
  ZoomIn,
  ZoomOut,
  Check,
  X,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/DatePicker";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

// ── Types & Config ────────────────────────────────────────────────────────────

type EventCategory = "meeting" | "announcement" | "celebration" | "company" | "other" | "bank_holiday";

const CATEGORY_CONFIG: Record<EventCategory, {
  label: string;
  color: string;
  dotColor: string;
  gradientClass: string;
  icon: React.ElementType;
}> = {
  meeting: {
    label: "Team Meeting",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    dotColor: "bg-blue-500",
    gradientClass: "from-blue-600 to-blue-800",
    icon: Users,
  },
  announcement: {
    label: "Announcement",
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    dotColor: "bg-amber-500",
    gradientClass: "from-amber-500 to-orange-700",
    icon: Megaphone,
  },
  celebration: {
    label: "Celebration",
    color: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
    dotColor: "bg-pink-500",
    gradientClass: "from-pink-500 to-rose-700",
    icon: PartyPopper,
  },
  company: {
    label: "Company Event",
    color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    dotColor: "bg-purple-500",
    gradientClass: "from-purple-600 to-violet-800",
    icon: Briefcase,
  },
  other: {
    label: "Other",
    color: "bg-muted text-muted-foreground",
    dotColor: "bg-muted-foreground",
    gradientClass: "from-slate-500 to-slate-700",
    icon: CalendarDays,
  },
  bank_holiday: {
    label: "Bank Holiday",
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    dotColor: "bg-red-500",
    gradientClass: "from-red-500 to-red-700",
    icon: Flag,
  },
};

// ── Edit Form ────────────────────────────────────────────────────────────────

interface EditForm {
  title: string;
  date: string;
  endDate: string;
  time: string;
  category: EventCategory;
  description: string;
  location: string;
  organizer: string;
  link: string;
  allDay: boolean;
}

function eventToForm(ev: CalendarEvent): EditForm {
  return {
    title: ev.title,
    // Always normalise to YYYY-MM-DD — pg DATE columns serialize as ISO timestamps
    date: ev.date ? ev.date.substring(0, 10) : format(new Date(), "yyyy-MM-dd"),
    endDate: ev.endDate ? ev.endDate.substring(0, 10) : "",
    time: ev.time ?? "",
    category: ev.category as EventCategory,
    description: ev.description ?? "",
    location: ev.location ?? "",
    organizer: ev.organizer ?? "",
    link: ev.link ?? "",
    allDay: ev.allDay,
  };
}

// ── Canvas crop helper ────────────────────────────────────────────────────────

async function getCroppedBlob(imageSrc: string, pixelCrop: Area): Promise<File> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", reject);
    image.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d")!;

  ctx.drawImage(
    img,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Canvas toBlob failed"));
        resolve(new File([blob], "cover.jpg", { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92,
    );
  });
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CorporateCalendarEvent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditForm | null>(null);

  // Crop dialog state
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const canManage = user?.role.mainRole === "admin" || user?.role.mainRole === "hr";

  const { data: event, isLoading, error } = useQuery<CalendarEvent>({
    queryKey: ["corporate-event", id],
    queryFn: () => apiGetCalendarEvent(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Omit<CalendarEvent, "id">) => apiUpdateCalendarEvent(id!, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["corporate-event", id] });
      qc.invalidateQueries({ queryKey: ["corporate-events"] });
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      setEditOpen(false);
      toast({ title: "Event updated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDeleteCalendarEvent(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["corporate-events"] });
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      navigate("/corporate-calendar");
    },
  });

  const coverMutation = useMutation({
    mutationFn: (file: File) => apiUploadEventCoverImage(id!, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["corporate-event", id] });
      qc.invalidateQueries({ queryKey: ["corporate-events"] });
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      toast({ title: "Cover photo updated" });
    },
    onError: () => toast({ title: "Upload failed", variant: "destructive" }),
  });

  function openEdit() {
    if (event) {
      setEditForm(eventToForm(event));
      setEditOpen(true);
    }
  }

  function handleSaveEdit() {
    if (!editForm || !event) return;
    updateMutation.mutate({
      title: editForm.title,
      date: editForm.date,
      endDate: editForm.endDate || null,
      category: editForm.category,
      description: editForm.description || null,
      allDay: editForm.allDay,
      time: editForm.allDay ? null : (editForm.time || null),
      location: editForm.location || null,
      organizer: editForm.organizer || null,
      link: editForm.link || null,
      createdBy: event.createdBy,
      coverImageUrl: event.coverImageUrl,
    });
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  async function handleCropConfirm() {
    if (!cropSrc || !croppedAreaPixels) return;
    try {
      const file = await getCroppedBlob(cropSrc, croppedAreaPixels);
      coverMutation.mutate(file);
      setCropSrc(null);
    } catch {
      toast({ title: "Crop failed", variant: "destructive" });
    }
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-72 w-full rounded-xl" />
          <div className="space-y-3">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Card className="shadow-card">
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (error || !event) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto text-center py-20">
          <CalendarDays className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold">{t("corporateCalendar.eventNotFound", "Event not found")}</h2>
          <Button variant="link" onClick={() => navigate("/corporate-calendar")}>
            {t("corporateCalendar.backBtn", "Back to Calendar")}
          </Button>
        </div>
      </AppLayout>
    );
  }

  const cfg = CATEGORY_CONFIG[event.category as EventCategory] ?? CATEGORY_CONFIG.other;
  const Icon = cfg.icon;
  const eventDate = parseISO(event.date);
  const endDate = event.endDate ? parseISO(event.endDate) : null;
  const isMultiDay = endDate && !isSameDay(eventDate, endDate);

  const dateLabel = isMultiDay
    ? `${format(eventDate, "EEE, d MMM yyyy")} – ${format(endDate!, "EEE, d MMM yyyy")}`
    : format(eventDate, "EEEE, d MMMM yyyy");

  return (
    <AppLayout>
      <div className="w-full space-y-6">
        {/* Breadcrumb */}
        <button
          onClick={() => navigate("/corporate-calendar")}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {t("corporateCalendar.breadcrumb", "Corporate Calendar")}
        </button>

        {/* Cover Image / Banner */}
        <div className="relative h-64 sm:h-80 rounded-xl overflow-hidden">
          {event.coverImageUrl ? (
            <img
              src={event.coverImageUrl}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${cfg.gradientClass}`} />
          )}
          {/* Bottom overlay for text */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Category + Title overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <Badge className={`${cfg.color} mb-3 border-0`}>
              <Icon className="h-3 w-3 mr-1" />
              {t(`corporateCalendar.categories.${event.category}`, cfg.label)}
            </Badge>
            <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
              {event.title}
            </h1>
          </div>

          {/* Cover photo upload button (HR/admin) */}
          {canManage && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={coverMutation.isPending}
                className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/50 hover:bg-black/70 text-white text-xs font-medium transition-colors backdrop-blur-sm"
              >
                <Camera className="h-3.5 w-3.5" />
                {coverMutation.isPending ? t("corporateCalendar.actions.uploading", "Uploading…") : event.coverImageUrl ? t("corporateCalendar.actions.changePhoto", "Change Photo") : t("corporateCalendar.actions.addPhoto", "Add Cover Photo")}
              </button>
            </>
          )}
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
          {/* Main details card */}
          <Card className="shadow-card">
            <CardContent className="p-6 space-y-5">
              {/* Date */}
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <CalendarDays className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{t("corporateCalendar.details.date", "Date")}</p>
                  <p className="text-sm font-medium text-foreground">{dateLabel}</p>
                  {!event.allDay && event.time && (
                    <p className="text-xs text-muted-foreground mt-0.5">{event.time}</p>
                  )}
                  {event.allDay && (
                    <p className="text-xs text-muted-foreground mt-0.5">{t("corporateCalendar.details.allDay", "All day")}</p>
                  )}
                </div>
              </div>

              {/* Location */}
              {event.location && (
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{t("corporateCalendar.details.location", "Location")}</p>
                    <p className="text-sm font-medium text-foreground">{event.location}</p>
                  </div>
                </div>
              )}

              {/* Time (if not all-day and location is empty) */}
              {!event.allDay && event.time && !event.location && (
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Clock className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{t("corporateCalendar.details.time", "Time")}</p>
                    <p className="text-sm font-medium text-foreground">{event.time}</p>
                  </div>
                </div>
              )}

              {/* Organizer */}
              {event.organizer && (
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{t("corporateCalendar.details.organizer", "Organizer")}</p>
                    <p className="text-sm font-medium text-foreground">{event.organizer}</p>
                  </div>
                </div>
              )}

              {/* Link */}
              {event.link && (
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Link2 className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{t("corporateCalendar.details.link", "Event Link")}</p>
                    <a
                      href={event.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                    >
                      {event.link.replace(/^https?:\/\//, "").split("/")[0]}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              )}

              {/* Description */}
              {event.description && (
                <>
                  <div className="border-t border-border" />
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t("corporateCalendar.details.about", "About this event")}</p>
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                      {event.description}
                    </p>
                  </div>
                </>
              )}

              {/* Empty state for employees */}
              {!event.description && !event.location && !event.organizer && !event.link && !canManage && (
                <p className="text-sm text-muted-foreground italic">{t("corporateCalendar.details.noDetails", "No additional details provided.")}</p>
              )}

              {/* Add details prompt for HR/admin */}
              {!event.description && !event.location && !event.organizer && !event.link && canManage && (
                <button
                  onClick={openEdit}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  {t("corporateCalendar.details.addDetails", "Add event details (location, description, organizer…)")}
                </button>
              )}
            </CardContent>
          </Card>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Meta */}
            <Card className="shadow-card">
              <CardContent className="p-4 space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t("corporateCalendar.details.category", "Category")}</p>
                  <Badge className={cfg.color}>
                    <Icon className="h-3 w-3 mr-1" />
                    {t(`corporateCalendar.categories.${event.category}`, cfg.label)}
                  </Badge>
                </div>
                {event.createdBy && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">{t("corporateCalendar.details.addedBy", "Added by")}</p>
                    <p className="text-sm text-foreground">{event.createdBy}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions (HR/admin only) */}
            {canManage && (
              <Card className="shadow-card">
                <CardContent className="p-4 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">{t("corporateCalendar.details.actionsTitle", "Actions")}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    onClick={openEdit}
                  >
                    <Edit2 className="h-3.5 w-3.5 mr-2" />
                    {t("corporateCalendar.actions.edit", "Edit Event")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setDeleteOpen(true)}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                    {t("corporateCalendar.actions.delete", "Delete Event")}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* ── Edit Dialog ─────────────────────────────────────────────────────── */}
      {canManage && editForm && (
        <Dialog open={editOpen} onOpenChange={(v) => { if (!v) setEditOpen(false); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("corporateCalendar.form.editTitle", "Edit Event")}</DialogTitle>
              <DialogDescription>{t("corporateCalendar.form.editDesc", "Update the details for this event.")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-1">
              <div className="space-y-1.5">
                <Label>{t("corporateCalendar.form.titleLabel", "Event Title *")}</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm((p) => p && ({ ...p, title: e.target.value }))}
                  placeholder={t("corporateCalendar.form.titlePlaceholder", "e.g. All-Hands Meeting")}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("corporateCalendar.form.startDate", "Start Date *")}</Label>
                  <DatePicker
                    value={editForm.date}
                    onChange={(v) => setEditForm((p) => p && ({ ...p, date: v }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("corporateCalendar.form.endDate", "End Date")}</Label>
                  <DatePicker
                    value={editForm.endDate}
                    onChange={(v) => setEditForm((p) => p && ({ ...p, endDate: v }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("corporateCalendar.form.time", "Time")}</Label>
                  <Input
                    type="time"
                    value={editForm.time}
                    disabled={editForm.allDay}
                    onChange={(e) => setEditForm((p) => p && ({ ...p, time: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("corporateCalendar.form.category", "Category")}</Label>
                  <Select
                    value={editForm.category}
                    onValueChange={(v) => setEditForm((p) => p && ({ ...p, category: v as EventCategory }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.entries(CATEGORY_CONFIG) as [EventCategory, typeof CATEGORY_CONFIG[EventCategory]][])
                        .filter(([key]) => key !== "bank_holiday")
                        .map(([key, c]) => (
                          <SelectItem key={key} value={key}>{t(`corporateCalendar.categories.${key}`, c.label)}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="editAllDay"
                  checked={editForm.allDay}
                  onChange={(e) => setEditForm((p) => p && ({ ...p, allDay: e.target.checked }))}
                  className="h-4 w-4"
                />
                <Label htmlFor="editAllDay" className="cursor-pointer font-normal">{t("corporateCalendar.form.allDayCheck", "All day event")}</Label>
              </div>

              <div className="space-y-1.5">
                <Label>{t("corporateCalendar.form.locationLabel", "Location")}</Label>
                <Input
                  value={editForm.location}
                  onChange={(e) => setEditForm((p) => p && ({ ...p, location: e.target.value }))}
                  placeholder={t("corporateCalendar.form.locationPlaceholder", "e.g. Conference Room A, Zoom, Amsterdam")}
                />
              </div>

              <div className="space-y-1.5">
                <Label>{t("corporateCalendar.form.organizerLabel", "Organizer")}</Label>
                <Input
                  value={editForm.organizer}
                  onChange={(e) => setEditForm((p) => p && ({ ...p, organizer: e.target.value }))}
                  placeholder={t("corporateCalendar.form.organizerPlaceholder", "e.g. Sarah Johnson, HR Team")}
                />
              </div>

              <div className="space-y-1.5">
                <Label>{t("corporateCalendar.form.linkLabel", "Event Link")}</Label>
                <Input
                  value={editForm.link}
                  onChange={(e) => setEditForm((p) => p && ({ ...p, link: e.target.value }))}
                  placeholder={t("corporateCalendar.form.linkPlaceholder", "https://zoom.us/j/...")}
                  type="url"
                />
              </div>

              <div className="space-y-1.5">
                <Label>{t("corporateCalendar.form.descLabel", "Description")}</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((p) => p && ({ ...p, description: e.target.value }))}
                  placeholder={t("corporateCalendar.form.descPlaceholder", "Tell people what this event is about…")}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>{t("common.cancel", "Cancel")}</Button>
              <Button
                onClick={handleSaveEdit}
                disabled={!editForm.title.trim() || updateMutation.isPending}
                className="gradient-brand text-primary-foreground"
              >
                {t("corporateCalendar.actions.save", "Save Changes")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Crop Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={!!cropSrc} onOpenChange={(v) => { if (!v) setCropSrc(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("corporateCalendar.dialogs.cropTitle", "Crop Cover Photo")}</DialogTitle>
            <DialogDescription>{t("corporateCalendar.dialogs.cropDesc", "Drag to reposition and use the slider to zoom in or out.")}</DialogDescription>
          </DialogHeader>

          {/* Cropper container — must have a fixed height */}
          <div className="relative w-full h-64 sm:h-80 rounded-lg overflow-hidden bg-black">
            {cropSrc && (
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={16 / 6}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>

          {/* Zoom controls */}
          <div className="flex items-center gap-3 px-1">
            <button
              onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
              className="p-1.5 rounded-md hover:bg-muted transition-colors"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-4 w-4 text-muted-foreground" />
            </button>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-1.5 accent-primary cursor-pointer"
              aria-label="Zoom level"
            />
            <button
              onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
              className="p-1.5 rounded-md hover:bg-muted transition-colors"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCropSrc(null)}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleCropConfirm}
              disabled={coverMutation.isPending}
              className="gradient-brand text-primary-foreground"
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              {coverMutation.isPending ? t("corporateCalendar.actions.uploading", "Uploading…") : t("corporateCalendar.actions.applyCrop", "Apply & Upload")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm ───────────────────────────────────────────────────── */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("corporateCalendar.dialogs.deleteTitle", "Delete event?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("corporateCalendar.dialogs.deleteDesc", { title: event.title, defaultValue: `"${event.title}" will be permanently removed from the calendar.` })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
